import { useMutation, UseMutationResult, useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import CreateDeviceCommand from 'features/device/domain/models/create-device-command';
import Device from 'features/device/domain/models/device';
import GetLinkableGatewaysResponse from 'features/device/domain/models/get-linkable-gateways-response';
import GetLinkableSystemsResponse from 'features/device/domain/models/get-linkable-systems-response';
import ReadDevicesCommand from 'features/device/domain/models/read-devices-command';
import ReadDevicesResponse from 'features/device/domain/models/read-devices-response';
import ReadFilterValuesCommand from 'features/device/domain/models/read-filter-values-command';
import ReadFilterValuesResponse from 'features/device/domain/models/read-filter-values-response';
import UpdateDeviceCommand from 'features/device/domain/models/update-device-command';
import ValidateBLEBeaconCommand from 'features/device/domain/models/validate-ble-beacon-command';
import ValidateDeviceExternalIdCommand from 'features/device/domain/models/validate-device-functional-id-command';
import ValidateDeviceNameCommand from 'features/device/domain/models/validate-device-name-command';
import ValidateIntercomMappingButtonIdCommand from 'features/device/domain/models/validate-intercom-mapping-button-id-command';

const baseUrl = '/api/v1/device';
const baseUrlV2 = '/api/v2/device';

enum DeviceQueryPaths {
  CreateDevice = 'CreateDevice',
  ReadDevices = 'ReadDevices',
  ReadDevice = 'ReadDevice',
  UpdateDevice = 'UpdateDevice',
  DeleteDevice = 'DeleteDevice',
  ReadFilterValues = 'ReadFilterValues',
  ValidateDeviceName = 'ValidateDeviceName',
  ValidateDeviceExternalId = 'ValidateDeviceExternalId',
  ValidateBLEBeacon = 'ValidateBLEBeacon',
  GetLinkableGateways = 'GetLinkableGateways',
  GetLinkableSystems = 'GetLinkableSystems',
  ValidateIntercomMappingButtonId = 'ValidateIntercomMappingButtonId',
}

const createDevice = async (axios: AxiosInstance, body: CreateDeviceCommand): Promise<Device> => {
  const response = await axios.post(`${baseUrlV2}/${DeviceQueryPaths.CreateDevice}`, body);
  return response.data;
};

const readDevices = async (axios: AxiosInstance, command: ReadDevicesCommand): Promise<ReadDevicesResponse> => {
  const response = await axios.post(`${baseUrlV2}/${DeviceQueryPaths.ReadDevices}`, command);
  return response.data;
};

const readDevice = async (axios: AxiosInstance, deviceId: string, signal?: AbortSignal): Promise<Device> => {
  const response = await axios.get(`${baseUrlV2}/${DeviceQueryPaths.ReadDevice}/${deviceId}`, { signal });
  return response.data;
};

const updateDevice = async (axios: AxiosInstance, body: UpdateDeviceCommand): Promise<Device> => {
  const response = await axios.put(`${baseUrlV2}/${DeviceQueryPaths.UpdateDevice}`, body);
  return response.data;
};

const deleteDevice = async (axios: AxiosInstance, id: string): Promise<void> => {
  await axios.delete(`${baseUrl}/${DeviceQueryPaths.DeleteDevice}/${id}`);
};

const readFilterValues = async (axios: AxiosInstance, command?: ReadFilterValuesCommand): Promise<ReadFilterValuesResponse> => {
  const response = await axios.post(`${baseUrl}/${DeviceQueryPaths.ReadFilterValues}`, command ?? {});
  return response.data;
};

export const validateDeviceName = async (axios: AxiosInstance, body: ValidateDeviceNameCommand): Promise<void> => {
  await axios.post(`${baseUrl}/${DeviceQueryPaths.ValidateDeviceName}`, body);
};

export const useValidateDeviceName = (): UseMutationResult<void, Error, ValidateDeviceNameCommand, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [DeviceQueryPaths.ValidateDeviceName],
    mutationFn: (body: ValidateDeviceNameCommand) => validateDeviceName(authenticatedInstance, body),
  });
};

export const validateDeviceExternalId = async (axios: AxiosInstance, body: ValidateDeviceExternalIdCommand): Promise<void> => {
  await axios.post(`${baseUrlV2}/${DeviceQueryPaths.ValidateDeviceExternalId}`, body);
};

export const useValidateDeviceExternalId = (): UseMutationResult<void, Error, ValidateDeviceExternalIdCommand, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [DeviceQueryPaths.ValidateDeviceExternalId],
    mutationFn: (body: ValidateDeviceExternalIdCommand) => validateDeviceExternalId(authenticatedInstance, body),
  });
};

export const validateBLEBeacon = async (axios: AxiosInstance, body: ValidateBLEBeaconCommand): Promise<void> => {
  await axios.post(`${baseUrl}/${DeviceQueryPaths.ValidateBLEBeacon}`, body);
};

export const validateIntercomMappingButtonId = async (
  axios: AxiosInstance,
  body: ValidateIntercomMappingButtonIdCommand,
): Promise<void> => {
  await axios.post(`${baseUrl}/${DeviceQueryPaths.ValidateIntercomMappingButtonId}`, body);
};

export const useValidateIntercomMappingButtonId = (): UseMutationResult<void, Error, ValidateIntercomMappingButtonIdCommand, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [DeviceQueryPaths.ValidateIntercomMappingButtonId],
    mutationFn: (body: ValidateIntercomMappingButtonIdCommand) => validateIntercomMappingButtonId(authenticatedInstance, body),
  });
};

export const useValidateBLEBeacon = (): UseMutationResult<void, Error, ValidateBLEBeaconCommand, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [DeviceQueryPaths.ValidateBLEBeacon],
    mutationFn: (body: ValidateBLEBeaconCommand) => validateBLEBeacon(authenticatedInstance, body),
  });
};

const getLinkableGateways = async (axios: AxiosInstance, deviceType?: string): Promise<GetLinkableGatewaysResponse> => {
  const url = deviceType
    ? `${baseUrl}/${DeviceQueryPaths.GetLinkableGateways}?deviceType=${deviceType}`
    : `${baseUrl}/${DeviceQueryPaths.GetLinkableGateways}`;
  const response = await axios.get(url);
  return response.data;
};

const getLinkableSystems = async (axios: AxiosInstance, deviceType?: string): Promise<GetLinkableSystemsResponse> => {
  const url = deviceType
    ? `${baseUrl}/${DeviceQueryPaths.GetLinkableSystems}?deviceType=${deviceType}`
    : `${baseUrl}/${DeviceQueryPaths.GetLinkableSystems}`;
  const response = await axios.get(url);
  return response.data;
};

export const useCreateDevice = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [DeviceQueryPaths.CreateDevice],
    mutationFn: (body: CreateDeviceCommand) => createDevice(authenticatedInstance, body),
  });
};

export const useReadDevices = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [DeviceQueryPaths.ReadDevices],
    mutationFn: (command: ReadDevicesCommand) => readDevices(authenticatedInstance, command),
  });
};

export const useReadDevice = (deviceId: string, enabled: boolean) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [DeviceQueryPaths.ReadDevice, deviceId],
    queryFn: ({ signal }) => readDevice(authenticatedInstance, deviceId, signal),
    enabled,
  });
};

export const useUpdateDevice = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [DeviceQueryPaths.UpdateDevice],
    mutationFn: (body: UpdateDeviceCommand) => updateDevice(authenticatedInstance, body),
  });
};

export const useDeleteDevice = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [DeviceQueryPaths.DeleteDevice],
    mutationFn: (id: string) => deleteDevice(authenticatedInstance, id),
  });
};

export const useReadFilterValuesMutation = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [DeviceQueryPaths.ReadFilterValues],
    mutationFn: (command?: ReadFilterValuesCommand) => readFilterValues(authenticatedInstance, command),
  });
};

export const useReadFilterValues = (command?: ReadFilterValuesCommand, enabled = false) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [DeviceQueryPaths.ReadFilterValues],
    queryFn: () => readFilterValues(authenticatedInstance, command),
    enabled,
  });
};

export const useGetLinkableGateways = (deviceType?: string, enabled?: boolean) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [DeviceQueryPaths.GetLinkableGateways, deviceType],
    queryFn: () => getLinkableGateways(authenticatedInstance, deviceType),
    enabled,
  });
};

export const useGetLinkableSystems = (deviceType?: string, enabled = false) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [DeviceQueryPaths.GetLinkableSystems, deviceType],
    queryFn: () => getLinkableSystems(authenticatedInstance, deviceType),
    enabled,
  });
};
